import React, { useEffect, useState } from 'react';
import {
  BookOpen, Clock, Award, CheckCircle, Circle, CalendarClock,
} from 'lucide-react';
import Modal from './Modal';
import { analyticsAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import './ModuleSummaryModal.css';

/**
 * Compact personal module analytics shown when a trainee clicks their own module row.
 * Shows per-content progress scoped to the authenticated trainee.
 * `moduleRef` accepts a numeric id, dashless UUID, or dashed UUID.
 */
const PersonalModuleSummaryModal = ({ isOpen, onClose, moduleRef }) => {
  const { showNotification } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !moduleRef) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsAPI.getPersonalModuleAnalytics(moduleRef)
      .then((response) => {
        if (cancelled) return;
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load module progress');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || 'Failed to load module progress';
        setError(msg);
        showNotification(msg, 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, moduleRef, showNotification]);

  const renderBody = () => {
    if (loading) {
      return <p style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-secondary)' }}>Loading…</p>;
    }
    if (error || !data) {
      return <p style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-secondary)' }}>{error || 'No data available.'}</p>;
    }

    const m = data.module;
    const e = data.enrollment;
    const contentItems = data.contentProgress || [];
    const cs = data.contentStats;
    const recentEvents = (data.recentEvents || []).slice(0, 8);

    const formatEventName = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div className="module-summary">
        {/* Module meta */}
        <div className="module-summary__meta">
          {m.category && <span className="badge badge-dark">{m.category}</span>}
          {m.level && <span className="badge badge-gray" style={{ marginLeft: 6 }}>{m.level}</span>}
          <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`} style={{ marginLeft: 6 }}>
            {m.status}
          </span>
          {m.durationMinutes ? <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>· {m.durationMinutes} min</span> : null}
        </div>

        {m.description && (
          <p className="module-summary__desc">{m.description}</p>
        )}

        {/* My stats */}
        <div className="module-summary__stats">
          {[
            { icon: Award, label: 'My Progress', value: `${e.progress}%`, sub: e.completed ? 'Completed' : 'In progress' },
            { icon: Clock, label: 'My Time Spent', value: `${e.timeSpentHours}h`, sub: null },
            { icon: BookOpen, label: 'Content Completed', value: `${cs.completedContent}/${cs.totalContent}`, sub: `${cs.contentCompletionRate}% completion` },
            { icon: CalendarClock, label: 'Last Accessed', value: e.lastAccessed ? new Date(e.lastAccessed).toLocaleDateString() : '—', sub: null },
          ].map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="module-summary__stat">
              <Icon size={16} className="module-summary__stat-icon" />
              <div className="module-summary__stat-body">
                <span className="module-summary__stat-value">{value}</span>
                <span className="module-summary__stat-label">{label}</span>
                {sub && <span className="module-summary__stat-sub">{sub}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Per-content progress */}
        {contentItems.length > 0 && (
          <div className="module-summary__top">
            <div className="module-summary__top-header">
              <span>Content Progress</span>
              <span className="badge badge-dark">{cs.completedContent}/{cs.totalContent} done</span>
            </div>
            {contentItems.map((c) => (
              <div key={c.contentId} className="module-summary__trainee">
                {c.completed
                  ? <CheckCircle size={14} style={{ color: '#91cc47', flexShrink: 0 }} />
                  : <Circle size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                }
                <span className="module-summary__trainee-name" style={{ flex: 1, minWidth: 0 }}>
                  {c.title}
                  <span style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                    {c.contentType || c.category}{c.durationMinutes ? ` · ${c.durationMinutes} min` : ''}
                  </span>
                </span>
                <span className="module-summary__trainee-pct" style={{ fontSize: '0.75rem' }}>
                  {c.completed ? 'Done' : `${Math.round(c.timeSpentSeconds / 60)}m spent`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Recent events */}
        {recentEvents.length > 0 && (
          <div className="module-summary__top" style={{ marginTop: 'var(--space-4)' }}>
            <div className="module-summary__top-header">
              <span>Recent Activity</span>
              <span className="badge badge-dark">{data.recentEvents.length} events</span>
            </div>
            {recentEvents.map((ev) => (
              <div key={ev.id} className="module-summary__trainee" style={{ fontSize: '0.75rem' }}>
                <span className="module-summary__trainee-name" style={{ flex: 1 }}>
                  {formatEventName(ev.eventName)}
                  {ev.sceneName ? <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>· {ev.sceneName}</span> : null}
                </span>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
                  {new Date(ev.eventTime).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={data?.module?.title || 'My Module Progress'}
      footer={
        <button className="btn btn-outline" onClick={onClose}>Close</button>
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default PersonalModuleSummaryModal;
